import { Injectable } from "@angular/core";

const STORAGE_KEY = "cb_redesign";

/**
 * Controls which visitors see the redesigned parallel pages. Deliberately not an
 * A/B test: there is no randomization and no analytics event. Whoever sends the
 * review link decides who sees the redesign by including `?design=redesign` (or
 * explicitly `?design=classic` to force the current experience). The choice is
 * sessionStorage-backed so it survives normal in-app navigation for the rest of
 * that visit without needing the query param repeated on every link.
 */
@Injectable({ providedIn: "root" })
export class RedesignVariantService {
  constructor() {
    this.captureFromUrl();
  }

  private captureFromUrl(): void {
    const params = new URLSearchParams(window.location.search);
    const design = params.get("design");
    if (design === "redesign" || design === "classic") {
      try {
        window.sessionStorage.setItem(STORAGE_KEY, design);
      } catch {
        // sessionStorage unavailable (private mode, etc.) - fall back to no persistence
        // for this visit; isRedesign() below reads directly from the URL param instead.
      }
    }
  }

  isRedesign(): boolean {
    const params = new URLSearchParams(window.location.search);
    const design = params.get("design");
    if (design === "redesign") {
      return true;
    }
    if (design === "classic") {
      return false;
    }
    try {
      return window.sessionStorage.getItem(STORAGE_KEY) === "redesign";
    } catch {
      return false;
    }
  }

  // Used by the floating on/off toggle - a manual override, same storage as the
  // `?design=` link so it takes effect on the very next navigation/guard check.
  setRedesign(value: boolean): void {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, value ? "redesign" : "classic");
    } catch {
      // sessionStorage unavailable - the toggle just won't persist across reloads.
    }
  }
}
